import { defineStore } from 'pinia'
import { getArticleById } from '../services/dataService.js'

export const useArticleCacheStore = defineStore('articleCache', {
    state: () => ({
        // 缓存文章数据，使用普通对象以支持持久化
        cachedArticles: {},
        // 访问顺序记录，用于LRU算法
        accessOrder: [],
        // 最大缓存数量
        maxCacheSize: 50,
        // 缓存统计
        cacheStats: {
            hits: 0,
            misses: 0,
            totalAccess: 0
        }
    }),

    getters: {
        // 获取缓存命中率
        hitRate: (state) => {
            if (state.cacheStats.totalAccess === 0) return 0
            return ((state.cacheStats.hits / state.cacheStats.totalAccess) * 100).toFixed(2)
        },

        // 获取当前缓存的文章数量
        cacheSize: (state) => Object.keys(state.cachedArticles).length,

        // 检查某篇文章是否已缓存
        isArticleCached: (state) => (articleId) => {
            return articleId.toString() in state.cachedArticles
        },

        // 获取缓存的文章列表（用于调试）
        cachedArticleIds: (state) => Object.keys(state.cachedArticles)
    },

    actions: {
        /**
         * 获取文章 - 优先从缓存获取，缓存未命中时从数据服务加载
         * @param {string|number} articleId 文章ID
         * @returns {Promise<Object|null>} 完整的文章对象
         */
        async getArticle(articleId) {
            const id = articleId.toString()
            this.cacheStats.totalAccess++

            // 检查缓存是否命中
            if (id in this.cachedArticles) {
                this.cacheStats.hits++

                // 更新访问顺序（LRU）
                this._updateAccessOrder(id)

                console.log(`📦 缓存命中: 文章 ${id}`)
                return this.cachedArticles[id]
            }

            // 缓存未命中，从数据服务加载
            this.cacheStats.misses++
            console.log(`🔄 缓存未命中，正在加载文章 ${id}`)

            try {
                const article = await getArticleById(articleId)

                if (article) {
                    // 将文章加入缓存
                    this._addToCache(id, article)
                    console.log(`✅ 文章 ${id} 已缓存`)
                }

                return article
            } catch (error) {
                console.error(`❌ 加载文章 ${id} 失败:`, error)
                return null
            }
        },

        /**
         * 预缓存文章 - 在用户点击前预先加载
         * @param {string|number} articleId 文章ID
         */
        async prefetchArticle(articleId) {
            const id = articleId.toString()

            // 如果已经缓存，直接返回
            if (id in this.cachedArticles) {
                console.log(`📦 文章 ${id} 已在缓存中`)
                return
            }

            console.log(`🔮 预缓存文章 ${id}`)

            try {
                const article = await getArticleById(articleId)
                if (article) {
                    this._addToCache(id, article)
                    console.log(`✅ 文章 ${id} 预缓存完成`)
                }
            } catch (error) {
                console.error(`❌ 预缓存文章 ${id} 失败:`, error)
            }
        },

        /**
         * 批量预缓存文章
         * @param {Array<string|number>} articleIds 文章ID数组
         */
        async prefetchArticles(articleIds) {
            const uncachedIds = articleIds
                .map(id => id.toString())
                .filter(id => !(id in this.cachedArticles))

            if (uncachedIds.length === 0) {
                console.log('🎯 所有文章都已缓存')
                return
            }

            console.log(`🔮 批量预缓存 ${uncachedIds.length} 篇文章`)

            // 并行预缓存（限制并发数）
            const batchSize = 3
            for (let i = 0; i < uncachedIds.length; i += batchSize) {
                const batch = uncachedIds.slice(i, i + batchSize)
                await Promise.all(batch.map(id => this.prefetchArticle(id)))
            }
        },

        /**
         * 将文章添加到缓存（内部方法）
         * @private
         */
        _addToCache(articleId, article) {
            const id = articleId.toString()

            // 如果缓存已满，移除最久未访问的文章
            if (Object.keys(this.cachedArticles).length >= this.maxCacheSize) {
                this._removeLeastRecentlyUsed()
            }

            // 添加到缓存
            this.cachedArticles[id] = {
                ...article,
                _cachedAt: Date.now() // 添加缓存时间戳
            }

            // 更新访问顺序
            this._updateAccessOrder(id)
        },

        /**
         * 更新访问顺序（内部方法）
         * @private
         */
        _updateAccessOrder(articleId) {
            const id = articleId.toString()

            // 移除旧的访问记录
            const existingIndex = this.accessOrder.indexOf(id)
            if (existingIndex > -1) {
                this.accessOrder.splice(existingIndex, 1)
            }

            // 添加到最前面（最近访问）
            this.accessOrder.unshift(id)
        },

        /**
         * 移除最久未访问的文章（内部方法）
         * @private
         */
        _removeLeastRecentlyUsed() {
            if (this.accessOrder.length === 0) return

            // 获取最久未访问的文章ID
            const lruArticleId = this.accessOrder.pop()

            // 从缓存中移除
            if (lruArticleId in this.cachedArticles) {
                delete this.cachedArticles[lruArticleId]
                console.log(`🗑️ LRU清理: 移除文章 ${lruArticleId}`)
            }
        },

        /**
         * 手动清除指定文章的缓存
         * @param {string|number} articleId 文章ID
         */
        removeFromCache(articleId) {
            const id = articleId.toString()

            if (id in this.cachedArticles) {
                delete this.cachedArticles[id]

                // 从访问顺序中移除
                const index = this.accessOrder.indexOf(id)
                if (index > -1) {
                    this.accessOrder.splice(index, 1)
                }

                console.log(`🗑️ 手动移除缓存: 文章 ${id}`)
            }
        },

        /**
         * 清空所有缓存
         */
        clearCache() {
            this.cachedArticles = {}
            this.accessOrder = []
            console.log('🗑️ 清空所有文章缓存')
        },

        /**
         * 更新缓存设置
         * @param {Object} settings 缓存设置
         */
        updateCacheSettings(settings) {
            if (settings.maxCacheSize && settings.maxCacheSize > 0) {
                this.maxCacheSize = settings.maxCacheSize

                // 如果新的最大值小于当前缓存数量，需要清理
                while (Object.keys(this.cachedArticles).length > this.maxCacheSize) {
                    this._removeLeastRecentlyUsed()
                }
            }
        },

        /**
         * 获取缓存统计信息
         */
        getCacheStats() {
            return {
                ...this.cacheStats,
                currentSize: this.cacheSize,
                maxSize: this.maxCacheSize,
                hitRate: this.hitRate + '%',
                cachedArticles: this.cachedArticleIds
            }
        },

        /**
         * 重置缓存统计
         */
        resetCacheStats() {
            this.cacheStats = {
                hits: 0,
                misses: 0,
                totalAccess: 0
            }
        }
    },

    // 注意：持久化需要额外插件，这里先注释
    // 可以后续添加 pinia-plugin-persistedstate 来支持持久化
    // persist: {
    //   key: 'mahiru-blog-article-cache',
    //   storage: sessionStorage,
    //   paths: ['cachedArticles', 'accessOrder']
    // }
}) 